import { internalReturnPath } from "../lib/onboardingIntent";
import { WebsiteHandoffRecovery } from "../components/WebsiteHandoffRecovery";
import { websiteHandoffParams, resumeWebsiteHandoff, WebsiteHandoffError } from "../lib/websiteHandoff";
import {
  isSignInWithEmailLink,
  sendSignInLinkToEmail,
  signInWithEmailLink,
} from "firebase/auth";
import { FirebaseError } from "firebase/app";
import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { Alert, AlertDescription } from "../components/ui/alert";
import { Button } from "../components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Input } from "../components/ui/input";
import { useAuth } from "../contexts/AuthContext";
import { isValidPrivateCode } from "../lib/privateCodes";
import { auth } from "../services/Firebase";

const FinishSignIn = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [handoffError, setHandoffError] = useState<WebsiteHandoffError | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { createCustomerIfNeeded, loading } = useAuth();
  const [isProcessing, setIsProcessing] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const [needsEmail, setNeedsEmail] = useState(false);
  const [emailInput, setEmailInput] = useState("");
  const [resendLoading, setResendLoading] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const MAX_RETRIES = 3;
  const CUSTOMER_LOAD_TIMEOUT = 10000; // 10 seconds timeout

  const returnToPath = useMemo(() => {
    const returnToFromQuery = internalReturnPath(searchParams.get("returnTo"));
    if (returnToFromQuery) {
      return returnToFromQuery;
    }

    const state = location.state as
      | {
          from?: { pathname?: string; search?: string; hash?: string };
        }
      | undefined;
    const from = state?.from;

    if (!from?.pathname) {
      return "/generate";
    }

    return `${from.pathname}${from.search || ""}${from.hash || ""}`;
  }, [location.state, searchParams]);

  // Use ref to track latest loading value for polling
  const loadingRef = useRef(loading);
  useEffect(() => {
    loadingRef.current = loading;
  }, [loading]);

  // Guard to prevent double sign-in attempts (email links are single-use)
  const signInAttemptedRef = useRef(false);

  const getAuthErrorMessage = (err: unknown): string | null => {
    if (err instanceof FirebaseError) {
      if (err.code === "auth/invalid-email") {
        return "This sign-in link does not match the email address entered. Please use the original email or request a new sign-in link.";
      }
      if (
        err.code === "auth/invalid-action-code" ||
        err.code === "auth/expired-action-code"
      ) {
        return "This sign-in link has expired or already been used. Please request a new sign-in link.";
      }
      return null;
    }

    if (err instanceof Error) {
      if (err.message.includes("auth/invalid-email")) {
        return "This sign-in link does not match the email address entered. Please use the original email or request a new sign-in link.";
      }
      if (
        err.message.includes("auth/invalid-action-code") ||
        err.message.includes("auth/expired-action-code")
      ) {
        return "This sign-in link has expired or already been used. Please request a new sign-in link.";
      }
    }

    return null;
  };

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!emailInput.trim()) {
      setError("Please enter your email address.");
      return;
    }

    // Store the email and restart the sign-in process
    window.localStorage.setItem("emailForSignIn", emailInput);
    // Reset the sign-in guard since user is explicitly providing email
    signInAttemptedRef.current = false;
    setNeedsEmail(false);
    setIsProcessing(true);
    setError(null);
  };

  const handleContinueLogin = () => {
    setError(null);
    signInAttemptedRef.current = false;
    setIsProcessing(true);
  };

  const handleResendLink = async () => {
    const email =
      searchParams.get("email") ||
      window.localStorage.getItem("emailForSignIn");
    if (!email) {
      navigate("/login");
      return;
    }

    setResendLoading(true);
    try {
      const newParams = new URLSearchParams();
      const firebaseParams = new Set(["apiKey", "oobCode", "mode", "lang"]);
      searchParams.forEach((value, key) => {
        if (!firebaseParams.has(key)) {
          newParams.set(key, value);
        }
      });
      if (!newParams.has("email")) {
        newParams.set("email", email);
      }
      if (!newParams.has("returnTo")) {
        newParams.set("returnTo", returnToPath);
      }

      const finishUrl = `${window.location.origin}/finish-signin?${newParams.toString()}`;
      await sendSignInLinkToEmail(auth, email, {
        url: finishUrl,
        handleCodeInApp: true,
      });
      window.localStorage.setItem("emailForSignIn", email);
      setResendSuccess(true);
    } catch (err) {
      console.error("Failed to resend sign-in link:", err);
      navigate("/login");
    } finally {
      setResendLoading(false);
    }
  };

  // Helper function to wait for customer data to load (by checking loading state)
  const waitForCustomerData = async (): Promise<boolean> => {
    const startTime = Date.now();

    return new Promise((resolve) => {
      const checkInterval = setInterval(() => {
        const elapsed = Date.now() - startTime;

        // Check if loading is complete using ref to get latest value
        if (!loadingRef.current) {
          clearInterval(checkInterval);
          resolve(true);
          return;
        }

        // Timeout after CUSTOMER_LOAD_TIMEOUT ms
        if (elapsed > CUSTOMER_LOAD_TIMEOUT) {
          clearInterval(checkInterval);
          console.warn(
            "[DEBUG] FinishSignIn: Timeout waiting for customer data"
          );
          resolve(false);
          return;
        }
      }, 100); // Check every 100ms
    });
  };

  useEffect(() => {
    const completeSignIn = async () => {
      try {
        // Guard: Prevent double sign-in attempts (email links can only be used once)
        if (signInAttemptedRef.current) {
          console.log(
            "[DEBUG] FinishSignIn: Sign-in already attempted, skipping"
          );
          return;
        }

        // First check if this is actually a valid email sign-in link
        if (!isSignInWithEmailLink(auth, window.location.href)) {
          setError("Invalid sign-in link. Please request a new one.");
          setIsProcessing(false);
          return;
        }

        // Get the email from URL parameters first (for cross-device support)
        let email = searchParams.get("email");

        // Fall back to localStorage if not in URL
        if (!email) {
          email = window.localStorage.getItem("emailForSignIn");
        }

        if (!email) {
          // Show email input form instead of browser prompt
          setNeedsEmail(true);
          setIsProcessing(false);
          return;
        }

        // Get and validate the private code
        const privateCode = searchParams.get("privateCode");
        console.log(
          "[DEBUG] FinishSignIn: Got privateCode from URL:",
          privateCode
        );

        const validatedPrivateCode =
          privateCode && isValidPrivateCode(privateCode)
            ? privateCode
            : undefined;
        console.log(
          "[DEBUG] FinishSignIn: Validated privateCode:",
          validatedPrivateCode
        );

        if (validatedPrivateCode) {
          console.log(
            "[DEBUG] FinishSignIn: Storing privateCode in sessionStorage:",
            validatedPrivateCode
          );
          window.sessionStorage.setItem(
            "pendingPrivateCode",
            validatedPrivateCode
          );
        }

        try {
          // Mark that we're attempting sign-in (prevent double attempts)
          signInAttemptedRef.current = true;

          // Attempt to sign in
          console.log("[DEBUG] FinishSignIn: About to sign in with email link");
          const userCredential = await signInWithEmailLink(
            auth,
            email,
            window.location.href
          );

          console.log(
            "[DEBUG] FinishSignIn: Sign in successful, creating customer"
          );
          console.log(
            "[DEBUG] FinishSignIn: Checking sessionStorage:",
            window.sessionStorage.getItem("pendingPrivateCode")
          );

          // Create customer if needed (this is a thin wrapper that just sets sessionStorage)
          await createCustomerIfNeeded(
            userCredential.user,
            validatedPrivateCode
          );

          // Wait for customer data to be loaded by AuthContext (via loading state)
          console.log(
            "[DEBUG] FinishSignIn: Waiting for customer data to load..."
          );
          const customerLoaded = await waitForCustomerData();

          if (!customerLoaded) {
            console.error(
              "[DEBUG] FinishSignIn: Customer data failed to load in time"
            );
            setError(
              "Failed to load user data. Please try refreshing the page."
            );
            setIsProcessing(false);
            return;
          }

          console.log(
            "[DEBUG] FinishSignIn: Customer data loaded successfully"
          );

          // Clean up
          window.localStorage.removeItem("emailForSignIn");

          const targetPath = await resumeWebsiteHandoff(userCredential.user, websiteHandoffParams(searchParams), returnToPath);
          navigate(targetPath, { replace: true });
        } catch (err) {
          if (err instanceof WebsiteHandoffError) { setHandoffError(err); setIsProcessing(false); return; }
          console.error("[DEBUG] FinishSignIn: Error during sign in:", err);

          const authErrorMessage = getAuthErrorMessage(err);

          // Don't retry auth errors - these are not transient
          if (authErrorMessage) {
            console.error(
              "[DEBUG] FinishSignIn: Auth error, not retrying:",
              err
            );
            setError(authErrorMessage);
            setIsProcessing(false);
            // Clear the guard so user can request a new link
            signInAttemptedRef.current = false;
          } else if (retryCount >= MAX_RETRIES) {
            // Only retry non-auth errors, and only up to MAX_RETRIES
            setError(
              err instanceof Error ? err.message : "Failed to complete sign in"
            );
            setIsProcessing(false);
          } else {
            // Retry for transient errors (network issues, etc.)
            console.log(
              `[DEBUG] FinishSignIn: Retrying (attempt ${retryCount + 1}/${MAX_RETRIES})`
            );
            setRetryCount((prev) => prev + 1);
            // Reset guard to allow retry
            signInAttemptedRef.current = false;
            setTimeout(() => completeSignIn(), 1000);
          }
        }
      } catch (err) {
        console.error("[DEBUG] FinishSignIn: Unexpected error:", err);
        setError(
          err instanceof Error ? err.message : "An unexpected error occurred"
        );
        setIsProcessing(false);
      }
    };

    if (isProcessing) {
      completeSignIn();
    }
  }, [
    createCustomerIfNeeded,
    navigate,
    searchParams,
    location.state,
    isProcessing,
    retryCount,
    returnToPath,
  ]);

  if (handoffError) return <WebsiteHandoffRecovery error={handoffError} />;

  if (needsEmail) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <Card className="w-full max-w-md">
          <CardHeader>
            <div className="flex justify-center mb-4">
              <img src="/icon.svg" alt="Company Logo" className="h-12 w-auto" />
            </div>
            <CardTitle className="text-center">Confirm Your Email</CardTitle>
            <CardDescription className="text-center">
              Please enter your email address to complete sign in
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleEmailSubmit} className="space-y-4">
              <div>
                <Input
                  type="email"
                  placeholder="Email address"
                  value={emailInput}
                  onChange={(e) => setEmailInput(e.target.value)}
                  required
                  className="w-full"
                  autoFocus
                />
              </div>
              <Button type="submit" className="w-full">
                Continue
              </Button>
              {error && (
                <Alert variant="destructive" className="mt-4">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    const isLinkExpiredError =
      error.includes("expired") || error.includes("already been used");
    const emailForResend =
      searchParams.get("email") ||
      window.localStorage.getItem("emailForSignIn");

    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-4">
          {resendSuccess ? (
            <Card className="w-full max-w-md">
              <CardHeader>
                <div className="flex justify-center mb-4">
                  <img
                    src="/icon.svg"
                    alt="Company Logo"
                    className="h-12 w-auto"
                  />
                </div>
                <CardTitle className="text-center">New link sent!</CardTitle>
                <CardDescription className="text-center">
                  We sent a new sign-in link to{" "}
                  <span className="font-medium">{emailForResend}</span>. Check
                  your inbox and click the link to continue.
                </CardDescription>
              </CardHeader>
            </Card>
          ) : (
            <>
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
              {isLinkExpiredError && emailForResend ? (
                <Button
                  onClick={handleResendLink}
                  className="w-full"
                  variant="outline"
                  disabled={resendLoading}
                >
                  {resendLoading
                    ? "Sending..."
                    : `Send new link to ${emailForResend}`}
                </Button>
              ) : (
                <Button
                  onClick={() => navigate("/login")}
                  className="w-full"
                  variant="outline"
                >
                  Request New Sign-In Link
                </Button>
              )}
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      {!isProcessing ? (
        <Card className="w-full max-w-md">
          <CardHeader>
            <div className="flex justify-center mb-4">
              <img src="/icon.svg" alt="Company Logo" className="h-12 w-auto" />
            </div>
            <CardTitle className="text-center">Continue Login</CardTitle>
            <CardDescription className="text-center">
              Click below to complete sign in securely.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={handleContinueLogin} className="w-full">
              Continue Login
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="text-center">
          <h2 className="text-lg font-semibold">Completing sign in...</h2>
          {retryCount > 0 && (
            <p className="text-gray-500 mt-2">
              Retrying sign-in... Attempt {retryCount} of {MAX_RETRIES}
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default FinishSignIn;
